import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  FileText, 
  AlertCircle, 
  RefreshCw,
  Search,
  X
} from 'lucide-react';
import api from 'services/api';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { TREE_METADATA_KEYS, filtrarArvorePlanoConta } from 'features/administrativo/utils/filtrarArvorePlanoConta';

const ArvorePlanosConta = () => {
  const [loading, setLoading] = useState(true);
  const [dadosArvore, setDadosArvore] = useState({});
  const [unidades, setUnidades] = useState([]);
  const [mapaUnidadeParaArvore, setMapaUnidadeParaArvore] = useState({});
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);
  const [erro, setErro] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));

  // Carregar dados do plano de conta
  const carregarPlanoConta = async () => {
    setLoading(true);
    setErro(null);
    
    try {
      const response = await api.get('/n8n/adm/plano_de_conta');
      
      if (response.data?.success) {
        const payload = response.data.data;

        // Caso venha por unidade: [{ unidade, data: { ...arvore... } }, ...]
        if (Array.isArray(payload)) {
          const mapa = {};
          const nomes = [];

          payload.forEach((item) => {
            if (!item || typeof item !== 'object') return;
            const unidade = item.unidade;
            const data = item.data;
            if (!unidade || typeof unidade !== 'string') return;
            if (!data || typeof data !== 'object') return;

            mapa[unidade] = data;
            nomes.push(unidade);
          });

          // Remover duplicadas mantendo ordem recebida (backend ja ordena).
          const uniqueNomes = [...new Set(nomes)];
          const unidadePadrao = uniqueNomes[0] || null;
          const arvorePadrao = unidadePadrao ? (mapa[unidadePadrao] || {}) : {};

          setUnidades(uniqueNomes);
          setMapaUnidadeParaArvore(mapa);
          setUnidadeSelecionada(unidadePadrao);
          setDadosArvore(arvorePadrao);

          const rootNodes = Object.keys(arvorePadrao || {});
          setExpandedNodes(new Set(rootNodes));
        } else {
          // Formato sem unidade: { ...arvore... }
          const arvore = payload && typeof payload === 'object' ? payload : {};
          setUnidades([]);
          setMapaUnidadeParaArvore({});
          setUnidadeSelecionada(null);
          setDadosArvore(arvore);

          const rootNodes = Object.keys(arvore || {});
          setExpandedNodes(new Set(rootNodes));
        }
      } else {
        setErro('Erro ao carregar plano de contas');
      }
    } catch (error) {
      console.error('Erro ao carregar plano de conta:', error);
      setErro(error.response?.data?.message || 'Erro ao carregar plano de contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanoConta();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Recarregar dados quando a empresa mudar
  useEffect(() => {
    const handleCompanyChange = () => {
      carregarPlanoConta();
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, []);

  // Toggle de expansão de nó
  const toggleNode = (codigo) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(codigo)) {
      newExpanded.delete(codigo);
    } else {
      newExpanded.add(codigo);
    }
    setExpandedNodes(newExpanded);
  };

  // Expandir/colapsar todos
  const toggleAll = () => {
    // Incluir códigos raiz e todos os filhos
    const rootCodes = Object.keys(dadosArvore).filter(key => {
      const item = dadosArvore[key];
      return typeof item === 'object' && item !== null && item.cod_planejamento;
    });
    const allCodes = [...rootCodes, ...getAllCodes(dadosArvore)];
    const uniqueCodes = [...new Set(allCodes)];
    
    if (expandedNodes.size === uniqueCodes.length) {
      setExpandedNodes(new Set());
    } else {
      setExpandedNodes(new Set(uniqueCodes));
    }
  };

  // Obter todos os códigos recursivamente
  const getAllCodes = (obj, codes = []) => {
    Object.keys(obj).forEach(key => {
      // Ignorar propriedades de metadados
      if (TREE_METADATA_KEYS.includes(key)) {
        return;
      }
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Se é um objeto filho (tem cod_planejamento), adicionar o código
        if (obj[key].cod_planejamento) {
          codes.push(obj[key].cod_planejamento);
        }
        // Continuar recursivamente
        getAllCodes(obj[key], codes);
      }
    });
    return codes;
  };

  const { arvore: dadosFiltrados, caminhos: caminhosDaBusca } = useMemo(
    () => filtrarArvorePlanoConta(dadosArvore, searchTerm),
    [dadosArvore, searchTerm]
  );

  // Durante a busca, abre o caminho até cada resultado; ao limpar, devolve a expansão de antes.
  const expansaoAntesDaBusca = useRef(null);
  useEffect(() => {
    if (searchTerm) {
      setExpandedNodes((atual) => {
        if (expansaoAntesDaBusca.current === null) {
          expansaoAntesDaBusca.current = atual;
        }
        return new Set(caminhosDaBusca);
      });
    } else if (expansaoAntesDaBusca.current !== null) {
      setExpandedNodes(expansaoAntesDaBusca.current);
      expansaoAntesDaBusca.current = null;
    }
  }, [searchTerm, caminhosDaBusca]);

  // Processar analiticos_ativos
  const processarAnaliticosAtivos = (analiticosAtivos) => {
    if (!analiticosAtivos || typeof analiticosAtivos !== 'string') {
      return [];
    }

    try {
      const parsed = JSON.parse(analiticosAtivos);
      if (!Array.isArray(parsed)) {
        return [];
      }

      // Filtrar apenas itens que têm pelo menos um campo válido (não-null e não-vazio)
      return parsed.filter(analitico => {
        if (!analitico || typeof analitico !== 'object') {
          return false;
        }

        // Verificar se tem pelo menos um campo válido
        const temIdValido = analitico.id !== null && analitico.id !== undefined;
        const temDescricaoValida = analitico.descricao && 
                                   analitico.descricao !== null && 
                                   analitico.descricao !== undefined && 
                                   analitico.descricao.trim() !== '';
        const temPrevisaoValida = analitico['previsao '] && 
                                  analitico['previsao '] !== null && 
                                  analitico['previsao '] !== undefined && 
                                  analitico['previsao '].trim() !== '';
        const temContaDominioValida = analitico.conta_dominio && 
                                      analitico.conta_dominio !== null && 
                                      analitico.conta_dominio !== undefined && 
                                      analitico.conta_dominio !== '';

        // Retornar true se pelo menos um campo for válido
        return temIdValido || temDescricaoValida || temPrevisaoValida || temContaDominioValida;
      });
    } catch (error) {
      console.warn('Erro ao processar analiticos_ativos:', error);
      return [];
    }
  };

  // Verificar se tem filhos (filhos são propriedades com cod_planejamento que não são metadados)
  const temFilhos = (item) => {
    if (!item || typeof item !== 'object') return false;
    
    return Object.keys(item).some(key => {
      // Ignorar propriedades de metadados
      if (TREE_METADATA_KEYS.includes(key)) {
        return false;
      }
      // Verificar se é um objeto filho (tem cod_planejamento)
      const filho = item[key];
      return typeof filho === 'object' && filho !== null && filho.cod_planejamento;
    });
  };

  // Renderizar nó da árvore
  const renderNode = (item, codigo, nivel = 0) => {
    if (!item || typeof item !== 'object') return null;

    const isExpanded = expandedNodes.has(codigo);
    const hasChildren = temFilhos(item);
    const analiticos = processarAnaliticosAtivos(item.analiticos_ativos);
    const temAnaliticos = analiticos.length > 0;

    const indentStep = isMobile ? 14 : 24;
    const maxIndent = isMobile ? 56 : 999;
    const paddingLeft = Math.min(nivel * indentStep, maxIndent);
    const tipoMap = {
      'A': { label: 'ATIVO', cor: 'text-blue-600', bg: 'bg-blue-500/10' },
      'P': { label: 'PASSIVO', cor: 'text-red-600', bg: 'bg-red-500/10' },
      'R': { label: 'RECEITA', cor: 'text-emerald-600', bg: 'bg-emerald-500/10' },
      'D': { label: 'DESPESA', cor: 'text-orange-600', bg: 'bg-orange-500/10' }
    };
    const tipoInfo = tipoMap[item.tipo] || { label: item.tipo || 'N/A', cor: 'text-[color:var(--text-muted)]', bg: 'bg-[color:var(--surface-muted)]' };
    const isRoot = nivel === 0;
    const isBranch = hasChildren || temAnaliticos;
    const depthTextClass =
      nivel === 0
        ? 'text-[15px] font-semibold tracking-[0.01em]'
        : nivel === 1
          ? 'text-sm font-semibold'
          : 'text-sm font-medium';
    const rowShellClassName = [
      'group flex min-h-[44px] w-full items-center gap-2 border-b border-[color:var(--header-control-border-subtle)] px-3 text-left transition-all duration-200 motion-reduce:transition-none',
      isBranch
        ? 'cursor-pointer hover:bg-[color:var(--surface-muted)]/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70 focus-visible:ring-inset'
        : 'cursor-default',
      isRoot
        ? 'bg-[color:var(--surface-muted)]/55 py-3'
        : nivel === 1
          ? 'bg-[color:var(--surface)]/75 py-2.5'
          : 'bg-transparent py-2'
    ].join(' ');
    const indicatorClassName = isBranch
      ? 'text-[color:var(--text-muted)] transition-transform duration-200 motion-reduce:transition-none group-hover:text-[color:var(--text-primary)]'
      : 'text-[color:var(--text-muted)]/70';
    const badgeClassName = `inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-[0.04em] ${tipoInfo.cor} ${tipoInfo.bg}`;
    const nodeContent = (
      <>
        <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-[color:var(--surface)]/55 border border-[color:var(--header-control-border-subtle)] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
          {isBranch ? (
            isExpanded ? (
              <ChevronDown className={`h-4 w-4 ${indicatorClassName}`} />
            ) : (
              <ChevronRight className={`h-4 w-4 ${indicatorClassName}`} />
            )
          ) : (
            <div className={`h-1.5 w-1.5 rounded-full bg-current ${indicatorClassName}`} />
          )}
        </div>

        {isMobile ? (
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className={`font-mono text-[13px] font-semibold tabular-nums text-[color:var(--text-primary)] ${isRoot ? '' : 'opacity-90'}`}>
                {item.cod_planejamento || codigo}
              </span>
              <span className={badgeClassName}>
                {tipoInfo.label}
              </span>
            </div>
            <p className={`mt-0.5 break-words pr-1 leading-snug text-[color:var(--text-primary)] ${depthTextClass}`}>
              {item.planejamento || 'Sem descrição'}
            </p>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <div className={`flex items-center gap-2.5 min-w-0 ${isRoot ? 'flex-wrap' : 'flex-wrap md:flex-nowrap'}`}>
              <span className={`font-mono text-[13px] font-semibold tabular-nums text-[color:var(--text-primary)] ${isRoot ? '' : 'opacity-90'}`}>
                {item.cod_planejamento || codigo}
              </span>
              <span className={`min-w-0 truncate text-[color:var(--text-primary)] ${depthTextClass}`}>
                {item.planejamento || 'Sem descrição'}
              </span>
              <span className={badgeClassName}>
                {tipoInfo.label}
              </span>
            </div>
          </div>
        )}
      </>
    );

    return (
      <div key={codigo} className="select-none">
        {isBranch ? (
          <button
            type="button"
            className={rowShellClassName}
            style={{ paddingLeft: `${paddingLeft + 12}px` }}
            onClick={() => toggleNode(codigo)}
            role="treeitem"
            aria-selected={isExpanded}
            aria-level={nivel + 1}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Recolher' : 'Expandir'} ${item.planejamento || codigo}`}
          >
            {nodeContent}
          </button>
        ) : (
          <div
            className={rowShellClassName}
            style={{ paddingLeft: `${paddingLeft + 12}px` }}
            role="treeitem"
            aria-selected={false}
            aria-level={nivel + 1}
          >
            {nodeContent}
          </div>
        )}

        {/* Conteúdo expandido: Analíticos primeiro, depois filhos */}
        {isBranch && isExpanded && (
          <div className={`${isRoot ? 'bg-[color:var(--surface)]/35' : ''} transition-opacity duration-200 motion-reduce:transition-none`}>
            {/* Renderizar analíticos ativos primeiro (se existirem) */}
            {temAnaliticos && analiticos.map((analitico, index) => (
              <div
                key={`analitico-${analitico.id || index}`}
                className="flex min-h-[44px] items-center gap-2 border-b border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/28 px-3 py-2 transition-colors duration-200 motion-reduce:transition-none hover:bg-[color:var(--surface-muted)]/45"
                style={{ paddingLeft: `${Math.min((nivel + 1) * indentStep, maxIndent) + 12}px` }}
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/50">
                  <div className="h-2 w-2 rounded-full bg-[color:var(--accent)]/75" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5 flex-wrap md:flex-nowrap">
                    {analitico.id && (
                      <span className="font-mono text-[12px] tabular-nums text-[color:var(--text-muted)]">
                        ID: {analitico.id}
                      </span>
                    )}
                    <span className="min-w-0 truncate text-sm font-medium text-[color:var(--text-primary)]">
                      {analitico.descricao || 'Sem descrição'}
                    </span>
                    {analitico['previsao '] && (
                      <span className="inline-flex items-center rounded-full bg-[color:var(--surface)]/85 px-2 py-0.5 text-[11px] font-semibold text-[color:var(--accent)]/90">
                        Previsão: {analitico['previsao ']}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Renderizar filhos normais depois dos analíticos */}
            {hasChildren && Object.keys(item)
              .filter(key => {
                // Filtrar apenas propriedades que são filhos (não são metadados)
                if (TREE_METADATA_KEYS.includes(key)) {
                  return false;
                }
                const filho = item[key];
                return typeof filho === 'object' && filho !== null && filho.cod_planejamento;
              })
              .sort((a, b) => {
                // Ordenar por cod_planejamento
                const codA = item[a]?.cod_planejamento || a;
                const codB = item[b]?.cod_planejamento || b;
                return codA.localeCompare(codB, undefined, { numeric: true, sensitivity: 'base' });
              })
              .map(key => {
                const filho = item[key];
                return renderNode(filho, filho.cod_planejamento, nivel + 1);
              })}
          </div>
        )}
      </div>
    );
  };

  const temDados = Object.keys(dadosFiltrados).length > 0;
  const rootCodes = Object.keys(dadosArvore).filter(key => {
    const item = dadosArvore[key];
    return typeof item === 'object' && item !== null && item.cod_planejamento;
  });
  const allCodes = [...rootCodes, ...getAllCodes(dadosArvore)];
  const uniqueCodes = [...new Set(allCodes)];
  const allExpanded = uniqueCodes.length > 0 && expandedNodes.size === uniqueCodes.length;

  if (loading) {
    return (
      <LoadingSpinner
        label="Carregando plano de contas"
        subtitle="Aguarde enquanto carregamos os dados..."
        className="min-h-[320px] w-full rounded-[22px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/85 shadow-[0_18px_50px_rgba(2,6,23,0.16)]"
        cardClassName="max-w-none w-full"
      />
    );
  }

  if (erro) {
    return (
      <div className="overflow-hidden rounded-[22px] border-x border-b border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/90 shadow-[0_18px_50px_rgba(2,6,23,0.16)]">
        <div className="border-b border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/55 px-5 py-4">
          <p className="text-sm font-semibold tracking-[0.04em] text-[color:var(--text-secondary)] uppercase">
            Plano de contas
          </p>
        </div>
        <div className="p-5">
          <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-600">Erro ao carregar plano de contas</h3>
                <p className="text-sm text-red-500 mt-1">{erro}</p>
              </div>
              <button
                onClick={carregarPlanoConta}
                className="ml-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[22px] border-x border-b border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/92 shadow-[0_18px_50px_rgba(2,6,23,0.16)]">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/92 px-4 py-3 backdrop-blur-sm md:static md:bg-[color:var(--surface-muted)]/52">
        <div className="flex flex-col gap-3 md:gap-3 xl:flex-row xl:items-center xl:gap-4">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
            <input
              type="text"
              placeholder="Buscar por código ou descrição..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-11 w-full rounded-xl border border-[color:var(--border)]/80 bg-[color:var(--surface)]/92 pl-10 pr-12 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition duration-200 motion-reduce:transition-none focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/70"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                aria-label="Limpar busca"
                className="absolute right-0 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-[color:var(--text-muted)] transition duration-200 motion-reduce:transition-none hover:bg-[color:var(--surface-muted)]/65 hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {unidades.length > 1 && (
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
                <label htmlFor="plano-conta-unidade" className="text-sm text-[color:var(--text-muted)] whitespace-nowrap">
                  Unidade
                </label>
                <select
                  id="plano-conta-unidade"
                  value={unidadeSelecionada || ''}
                  onChange={(e) => {
                    const novaUnidade = e.target.value;
                    const novaArvore = mapaUnidadeParaArvore[novaUnidade] || {};

                    setUnidadeSelecionada(novaUnidade);
                    setDadosArvore(novaArvore);

                    const rootNodes = Object.keys(novaArvore || {});
                    setExpandedNodes(new Set(rootNodes));
                  }}
                  className="h-11 min-w-0 flex-1 rounded-xl border border-[color:var(--border)]/80 bg-[color:var(--surface)]/92 px-3 text-sm text-[color:var(--text-primary)] transition duration-200 motion-reduce:transition-none focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/70"
                >
                  {unidades.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={toggleAll}
                aria-pressed={allExpanded}
                className="inline-flex h-11 items-center rounded-xl border border-[color:var(--border)]/80 bg-[color:var(--surface)]/92 px-3.5 text-sm font-medium text-[color:var(--text-primary)] transition duration-200 motion-reduce:transition-none hover:bg-[color:var(--surface-muted)]/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70 whitespace-nowrap"
              >
                {allExpanded ? 'Colapsar Tudo' : 'Expandir Tudo'}
              </button>
              <button
                onClick={carregarPlanoConta}
                aria-label="Atualizar plano de contas"
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-transparent text-[color:var(--text-muted)] transition duration-200 motion-reduce:transition-none hover:border-[color:var(--border)]/80 hover:bg-[color:var(--surface)]/92 hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70"
                title="Atualizar"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Árvore */}
      <div>
        {!temDados ? (
          <div className="px-5 py-14 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[color:var(--border)]/70 bg-[color:var(--surface-muted)]/45">
              <FileText className="h-6 w-6 text-[color:var(--text-muted)]" />
            </div>
            <p className="text-sm font-medium text-[color:var(--text-secondary)]">
              {searchTerm ? 'Nenhum resultado encontrado para a busca.' : 'Nenhum plano de conta encontrado.'}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="inline-flex h-11 items-center rounded-xl border border-[color:var(--border)]/80 bg-[color:var(--surface)]/92 px-3.5 text-sm font-medium text-[color:var(--text-primary)] transition duration-200 motion-reduce:transition-none hover:bg-[color:var(--surface-muted)]/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70"
                >
                  Limpar busca
                </button>
              ) : (
                <button
                  type="button"
                  onClick={carregarPlanoConta}
                  className="inline-flex h-11 items-center rounded-xl border border-[color:var(--border)]/80 bg-[color:var(--surface)]/92 px-3.5 text-sm font-medium text-[color:var(--text-primary)] transition duration-200 motion-reduce:transition-none hover:bg-[color:var(--surface-muted)]/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/70"
                >
                  Recarregar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div role="tree" aria-label="Árvore do plano de contas">
            {Object.keys(dadosFiltrados).map(codigo => {
              const item = dadosFiltrados[codigo];
              return renderNode(item, codigo, 0);
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArvorePlanosConta;
